/**
 * Zod schemas do **subsistema genérico de Forms** (ver
 * `docs/forms-subsystem.md`). Define o shape do JSON que vive na
 * coluna `form_templates.schema` (hoje `onboarding_templates.schema`
 * até M1-01d-0).
 *
 * **Shape estável** — todos os PRs do subsistema (renderer M1-01b-ii,
 * builder M1-01d-i/ii, distribuição M1-01d-iii) consomem este
 * contrato. Novos tipos de campo acrescentam variants ao
 * `formFieldSchema` sem quebrar os existentes.
 */
import { z } from 'zod';

// ─── Tipos de campo suportados ──────────────────────────────────────
//
// 9 tipos suportados. `multiselect` entrou antecipado (era M1-01d-ii)
// porque o template canônico (0039) usa na pergunta "processos
// existentes" — sem ele o parse do Zod falhava no renderer.
// Demais tipos avançados (`number`, `date`, `scale`, `file`,
// `signature`) entram em PRs futuros sem quebrar este contrato.
export const FORM_FIELD_TYPES = [
  'text',
  'textarea',
  'email',
  'tel',
  'cnpj',
  'cep',
  'select',
  'radio',
  'multiselect',
] as const;

export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

// ─── Lógica condicional ─────────────────────────────────────────────
//
// `visible_if` é a base do comportamento condicional. Exemplo:
// `{ field: 'marketing_ativo', equals: 'sim' }` → campo só aparece se
// o valor de `marketing_ativo` for exatamente 'sim'. Três operadores
// cobrem 100% dos casos do template atual. Expandir adiciona novos
// operadores (ex: `not_equals`, `gt`) sem migração.
export const formVisibleIfSchema = z
  .object({
    field: z.string().min(1),
    equals: z.string().optional(),
    includes: z.string().optional(), // para multiselect no futuro
    not_empty: z.boolean().optional(),
  })
  .refine((v) => v.equals !== undefined || v.includes !== undefined || v.not_empty !== undefined, {
    message: 'visible_if precisa de ao menos um operador (equals/includes/not_empty)',
  });

export type FormVisibleIf = z.infer<typeof formVisibleIfSchema>;

// ─── Option de select/radio ─────────────────────────────────────────
export const formFieldOptionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
});

export type FormFieldOption = z.infer<typeof formFieldOptionSchema>;

// ─── Field (pergunta individual) ────────────────────────────────────
//
// Campos opcionais `follow_up` e `other_field` usam `z.lazy` para
// permitir recursão — um campo condicional referencia outro
// FormField. Profundidade prática: 1 nível (follow_up não tem
// follow_up). Builder (M1-01d-ii) pode permitir aninhar mais fundo
// se aparecer caso de uso real; por ora, a validação aceita
// profundidade arbitrária mas o renderer renderiza só 1 nível.
// Nota: `z.lazy` com recursão exige tipagem explícita. Usamos
// `ZodTypeAny` e castamos no consumer — simpler que compatibilizar
// inferência optional (`string | undefined`) com interface TS
// (`description?: string`).
export const formFieldSchema: z.ZodType<FormField, z.ZodTypeDef, unknown> = z.lazy(() =>
  z
    .object({
      id: z
        .string()
        .min(1)
        .regex(/^[a-z][a-z0-9_]*$/, {
          message: 'id do campo deve começar com letra e conter só [a-z0-9_]',
        }),
      type: z.enum(FORM_FIELD_TYPES),
      label: z.string().min(1),
      description: z.string().optional(),
      required: z.boolean().default(false),
      placeholder: z.string().optional(),
      autocomplete: z.string().optional(),
      inputmode: z.enum(['numeric', 'tel', 'email', 'decimal', 'search']).optional(),
      maxlength: z.number().int().positive().optional(),
      /** Autofill via API externa. Implementação no renderer —
       * `brasilapi-cnpj` reusa `useCnpjLookup`, `brasilapi-cep` reusa
       * `useCepLookup`. */
      autofill: z.enum(['brasilapi-cnpj', 'brasilapi-cep']).optional(),
      /** Path para pré-popular com dado externo (ex: `lead.email`).
       * Resolvido no handler da edge function ao criar a submission. */
      prefill: z.string().optional(),
      /** Opções para select/radio (obrigatório para esses tipos). */
      options: z.array(formFieldOptionSchema).optional(),
      /** Se definido, o campo só aparece quando a condição é verdadeira. */
      visible_if: formVisibleIfSchema.optional(),
      /** Campo auxiliar — renderizado DEPOIS do principal quando a
       * condição em `follow_up.visible_if` for verdadeira (ex: "Se
       * marketing_ativo = sim, mostrar 'como funciona?'"). */
      follow_up: formFieldSchema.optional(),
      /** Alias semântico para `follow_up` em multiselect com opção
       * "outro". Compatibilidade com o template seedado (0039). */
      other_field: formFieldSchema.optional(),
    })
    .refine(
      (f) => {
        // select, radio e multiselect exigem options não-vazio.
        const needsOptions = f.type === 'select' || f.type === 'radio' || f.type === 'multiselect';
        if (needsOptions && (!f.options || f.options.length === 0)) {
          return false;
        }
        return true;
      },
      { message: 'select/radio/multiselect requerem pelo menos 1 option' },
    ),
);

// Precisa combinar `?` (propriedade ausente) E `| undefined` (valor
// explícito) — Zod `.optional()` tipa como ambos, e em `strict` +
// `exactOptionalPropertyTypes` esse detalhe importa para
// `z.ZodType<FormField>` aceitar o schema.
export interface FormField {
  id: string;
  type: FormFieldType;
  label: string;
  description?: string | undefined;
  required: boolean;
  placeholder?: string | undefined;
  autocomplete?: string | undefined;
  inputmode?: 'numeric' | 'tel' | 'email' | 'decimal' | 'search' | undefined;
  maxlength?: number | undefined;
  autofill?: 'brasilapi-cnpj' | 'brasilapi-cep' | undefined;
  prefill?: string | undefined;
  options?: FormFieldOption[] | undefined;
  visible_if?: FormVisibleIf | undefined;
  follow_up?: FormField | undefined;
  other_field?: FormField | undefined;
}

// ─── Section (agrupa campos) ────────────────────────────────────────
export const formSectionSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  description: z.string().optional(),
  questions: z.array(formFieldSchema).min(1, 'seção precisa de ao menos 1 pergunta'),
});

export type FormSection = z.infer<typeof formSectionSchema>;

// ─── Schema principal ───────────────────────────────────────────────
export const formSchemaSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  sections: z.array(formSectionSchema).min(1, 'form precisa de ao menos 1 seção'),
});

export type FormSchema = z.infer<typeof formSchemaSchema>;

// ─── Answers (a submissão em si) ────────────────────────────────────
//
// Cada chave é o `field.id`; valor depende do tipo:
//   - text/textarea/email/tel/cnpj/cep → string
//   - select/radio → string (value da option selecionada)
//   - (futuro) multiselect → string[]
//   - (futuro) number → number
//   - (futuro) date → string ISO YYYY-MM-DD
//
// Um `record<string, unknown>` com `z.unknown()` aceita qualquer
// forma — a validação concreta acontece no `validate.ts` que gera
// um Zod dinâmico a partir do `FormSchema` (garante tipos corretos
// por field.type e required).
export const formAnswersSchema = z.record(z.string(), z.unknown());
export type FormAnswers = z.infer<typeof formAnswersSchema>;
